import React, { useRef, useEffect, useState, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useFetcher, useNavigate } from '@remix-run/react'
import { ChevronUp, ChevronDown, Zap, Loader2 } from 'lucide-react'
import { Protocol } from 'pmtiles'
import mapData from '#other/mapa.json'
import { Button } from '#app/components/ui/button.tsx'
import { createRoot } from 'react-dom/client'
import { formatText, formatState } from '#app/utils/format-text.tsx'
import './custom-geocoder.css'
// import MapLibreGlDirections, {
// 	LoadingIndicatorControl,
// } from '@maplibre/maplibre-gl-directions'
// import { Separator } from '#app/components/ui/separator.tsx'
// import { Button, buttonVariants } from '#app/components/ui/button.tsx'
// import {
// 	Dialog,
// 	DialogContent,
// 	DialogDescription,
// 	DialogHeader,
// 	DialogTitle,
// 	DialogTrigger,
// } from '#app/components/ui/dialog'
// import { AutoComplete } from '#app/components/ui/autocomplete.tsx'
// import { Combobox } from './forms'

const decode = (encoded) => {
	const salt = 'bkWUdYqNoedWDz9RZhZL'
	const data = atob(encoded)
	const value = parseFloat(data.replace(salt, ''))
	return value
}

class CustomFullscreenControl extends maplibregl.FullscreenControl {
	_requestFullscreen() {
		this._togglePseudoFullScreen()
	}
	_exitFullscreen() {
		this._togglePseudoFullScreen()
	}
}

// Check for touch support to identify mobile/touch devices
const isMobileDevice =
	typeof window !== 'undefined' &&
	('ontouchstart' in window || navigator.maxTouchPoints > 0)

export default function Mapa({
	favorites,
	chargingStations,
	userLat,
	userLon,
	zoomLevel,
}) {
	// useEffect(() => {
	// 	let protocol = new Protocol()
	// 	maplibregl.addProtocol('pmtiles', protocol.tile)
	// 	return () => {
	// 		maplibregl.removeProtocol('pmtiles')
	// 	}
	// }, [])

	// class RouteControl {
	// 	onAdd(map) {
	// 		this.map = map
	// 		this.container = document.createElement('div')
	// 		this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group'
	// 		this.button = document.createElement('button')
	// 		this.button.innerHTML = '<img src="/route.svg" />'
	// 		this.button.type = 'button'
	// 		this.button.title = 'Rota'
	// 		this.button.onclick = () => {
	// 			setRouteModalOpen(!routeModalOpen)
	// 		}
	// 		this.container.appendChild(this.button)
	// 		return this.container
	// 	}

	// 	onRemove() {
	// 		this.container.parentNode.removeChild(this.container)
	// 		this.map = undefined
	// 	}
	// }

	class RadioFilterControl {
		onAdd(map) {
			this._map = map
			this._container = document.createElement('div')
			this._container.className = 'maplibregl-ctrl'

			const container = this._container
			const root = createRoot(container)

			//current power_filter query string
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')

			root.render(
				<RadioFilter
					currentPowerFilter={powerFilter}
					onChange={this.handleFilterChange}
				/>,
			)

			return this._container
		}

		handleFilterChange = (e) => {
			const searchParams = new URLSearchParams(window.location.search)
			if (!e.target.value) {
				searchParams.delete('power_filter')
			} else {
				searchParams.set('power_filter', e.target.value)
			}
			window.history.pushState(
				{},
				'',
				`${window.location.pathname}?${searchParams.toString()}`,
			)

			// Clear the loaded stations cache when filter changes
			setLoadedStations(new Map())

			// Trigger a new fetch by updating the filter state
			setFilter(e.target.value || 0)
		}

		onRemove() {
			this._container.parentNode.removeChild(this._container)
			this._map = undefined
		}
	}

	const mapContainer = useRef(null)
	const map = useRef(null)
	const mapRef = useRef(null)
	const [lng] = useState(decode(userLon))
	const [lat] = useState(decode(userLat))
	const [zoom] = useState(zoomLevel)
	const fetcher = useFetcher()
	const navigate = useNavigate()
	const [filter, setFilter] = useState(0)
	const [routeModalOpen, setRouteModalOpen] = useState()
	const interactionsSetupRef = useRef(false)

	//Departure
	// const [isDepartureDisabled, setDepartureDisabled] = useState(false)
	// const [departureValue, setDepartureValue] = useState()
	// const [departureQuery, setDepartureQuery] = useState()
	// const [departureOptions, setDepartureOptions] = useState([])

	//Arrival
	// const [isArrivalDisabled, setArrivalDisabled] = useState(false)
	// const [arrivalValue, setArrivalValue] = useState()
	// const [arrivalQuery, setArrivalQuery] = useState()

	const [loadedStations, setLoadedStations] = useState(new Map())
	const maxStations = 500 // Maximum stations to keep in memory

	const calculateScore = (positives, negatives) => {
		const total = (positives || 0) + (negatives || 0)
		if (total === 0) {
			return null // Return null if no ratings
		}
		// Simple weighted score: 1 + (positive ratio * 4)
		const score = 1 + ((positives || 0) / total) * 4
		return parseFloat(score.toFixed(1)) // Return score rounded to one decimal place
	}

	const [iconsLoaded, setIconsLoaded] = useState(false)

	// <-- Helper function to check if station is in bounds
	const isStationInBounds = useCallback((station, bounds) => {
		const lat =
			typeof station.latitude === 'string'
				? decode(station.latitude)
				: station.latitude
		const lng =
			typeof station.longitude === 'string'
				? decode(station.longitude)
				: station.longitude
		return (
			lng >= bounds._sw.lng &&
			lng <= bounds._ne.lng &&
			lat >= bounds._sw.lat &&
			lat <= bounds._ne.lat
		)
	}, []) // decode is stable, no dependency needed

	const RadioFilter = ({ onChange, currentPowerFilter }) => {
		const [isMinimized, setIsMinimized] = useState(!currentPowerFilter)
		const [selectedFilter, setSelectedFilter] = useState(
			currentPowerFilter || '',
		)

		// Update local state when currentPowerFilter changes from URL
		useEffect(() => {
			setSelectedFilter(currentPowerFilter || '')
		}, [currentPowerFilter])

		// Update global filter state when selectedFilter changes
		useEffect(() => {
			if (selectedFilter) {
				setFilter(selectedFilter)
			} else {
				setFilter(0) // Default to "Todas"
			}
		}, [selectedFilter])

		const handleFilterChange = (e) => {
			setSelectedFilter(e.target.value)
			onChange(e)
		}

		return (
			<div className="power-filter-container">
				<div
					className="power-filter-header"
					onClick={() => setIsMinimized(!isMinimized)}
				>
					<Zap className="power-filter-icon-left" />
					<span className="power-filter-title">Potência Média</span>
					{isMinimized ? (
						<ChevronDown className="power-filter-icon-right" />
					) : (
						<ChevronUp className="power-filter-icon-right" />
					)}
				</div>
				{!isMinimized && (
					<form className="power-filter-form">
						<div className="power-filter-option">
							<input
								type="radio"
								id="todos"
								name="power_filter"
								value=""
								checked={selectedFilter !== '20' && selectedFilter !== '50'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="todos">
								Todas
							</label>
						</div>
						<div className="power-filter-option">
							<input
								type="radio"
								id="20kw"
								name="power_filter"
								value="20"
								checked={selectedFilter === '20'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="20kw">
								&gt; 20 kW
							</label>
						</div>
						<div className="power-filter-option">
							<input
								type="radio"
								id="50kw"
								name="power_filter"
								value="50"
								checked={selectedFilter === '50'}
								onChange={handleFilterChange}
								className="power-filter-radio"
							/>
							<label className="power-filter-label" htmlFor="50kw">
								&gt; 50 kW
							</label>
						</div>
					</form>
				)}
			</div>
		)
	}

	const iconTypeToColor = {
		AR: 'vermelho',
		BR: 'vermelho',
		YR: 'vermelho',
		GR: 'vermelho',
		A: 'amarelo',
		B: 'amarelo',
		H: 'amarelo',
		Y: 'azul',
		G: 'verde',
	}

	const openLink = (url, id) => {
		const searchParams = new URLSearchParams(window.location.search)

		navigate(`/estacoes/${url}${id}?${searchParams.toString()}`, {
			preventScrollReset: true,
			state: { history: `/estacoes?${searchParams.toString()}` },
		})
	}

	// Variables for tooltip state
	let stationPopup = null
	let hoveredStationId = null

	useEffect(() => {
		if (mapRef.current) return

		map.current = new maplibregl.Map({
			container: mapContainer.current,
			style: mapData,
			// style:
			// 	'https://api.maptiler.com/maps/11347c1d-e020-4b1b-95be-1e11299e4b94/style.json?key=DBiR6978mpKK9VStp8FJ',
			center: [lng, lat],
			zoom: zoom,
			attributionControl: false,
			fadeDuration: 100,
		})

		map.current.on('load', async () => {
			mapRef.current = map.current

			const features = chargingStations.map((station) => ({
				type: 'Feature',
				properties: {
					id: station.id,
					url: station.url,
					name: station.name,
					averagePower: station.averagePower,
					score: station.score,
					positives: station.positives,
					negatives: station.negatives,
					city: station.city,
					state: station.state,
					icon_type: station.icon_type,
					icon: favorites.includes(station.id)
						? 'roxo'
						: iconTypeToColor[station.icon_type],
				},
				geometry: {
					type: 'Point',
					coordinates: [decode(station.longitude), decode(station.latitude)],
				},
			}))

			const bounds = map.current.getBounds()

			const nlat = bounds._ne.lat.toString()
			const nlng = bounds._ne.lng.toString()
			const slat = bounds._sw.lat.toString()
			const slng = bounds._sw.lng.toString()

			// get current power_filter query string - if it exists
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')
			let urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}`
			if (powerFilter) {
				urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}&power_filter=${powerFilter}`
			}

			fetcher.load(urlToLoad)

			const colors = ['verde', 'roxo', 'amarelo', 'azul', 'vermelho']

			// Load icons in parallel AND await completion
			Promise.all(
				colors.map(async (color) => {
					try {
						const image = await map.current.loadImage(`/${color}.png`)
						if (!map.current.hasImage(color)) {
							// Use addImage directly
							map.current.addImage(color, image.data)
						}
					} catch (error) {
						console.error(`Failed to load or add image for ${color}:`, error)
					}
				}),
			).then(() => {
				// Set iconsLoaded state after a brief delay
				setTimeout(() => {
					if (map.current) {
						setIconsLoaded(true)
						fetchStationsForBounds() // Initial fetch
					}
				}, 0)
			})

			// Define fetch function
			const fetchStationsForBounds = () => {
				if (!map.current) return // Guard against map not existing
				const bounds = map.current.getBounds()
				const nlat = bounds._ne.lat.toString()
				const nlng = bounds._ne.lng.toString()
				const slat = bounds._sw.lat.toString()
				const slng = bounds._sw.lng.toString()

				// get current power_filter query string
				const searchParams = new URLSearchParams(window.location.search)
				const powerFilter = searchParams.get('power_filter')

				let urlToLoad = `/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}`
				if (powerFilter) {
					urlToLoad += `&power_filter=${powerFilter}`
				}

				fetcher.load(urlToLoad)
			}

			// Add listeners for map movement
			map.current.on('moveend', fetchStationsForBounds)
			map.current.on('zoomend', fetchStationsForBounds)

			// Now add sources and layers AFTER icons are loaded and added
			map.current.addSource('stations', {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: features,
				},
			})

			// --- Layer Definitions (Bottom to Top) ---

			// Vermelho (Lowest Layer)
			map.current.addLayer({
				id: 'stations-vermelho',
				type: 'symbol',
				source: 'stations',
				layout: {
					'icon-image': '{icon}',
					'icon-size': 0.7,
					'icon-allow-overlap': true,
					'text-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-ignore-placement': true,
					'icon-anchor': 'bottom',
				},
				filter: ['in', ['get', 'icon'], ['literal', ['vermelho']]], // Only vermelho
			})

			// Amarelo
			map.current.addLayer({
				id: 'stations-amarelo',
				type: 'symbol',
				source: 'stations',
				layout: {
					'icon-image': '{icon}',
					'icon-size': 0.7,
					'icon-allow-overlap': true,
					'text-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-ignore-placement': true,
					'icon-anchor': 'bottom',
				},
				filter: ['in', ['get', 'icon'], ['literal', ['amarelo']]], // Only amarelo
			})

			// Verde
			map.current.addLayer({
				id: 'stations-verde',
				type: 'symbol',
				source: 'stations',
				layout: {
					'icon-image': '{icon}',
					'icon-size': 0.7,
					'icon-allow-overlap': true,
					'text-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-ignore-placement': true,
					'icon-anchor': 'bottom',
				},
				filter: ['in', ['get', 'icon'], ['literal', ['verde']]], // Only verde
			})

			// Azul
			map.current.addLayer({
				id: 'stations-azul',
				type: 'symbol',
				source: 'stations',
				layout: {
					'icon-image': '{icon}',
					'icon-size': 0.7,
					'icon-allow-overlap': true,
					'text-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-ignore-placement': true,
					'icon-anchor': 'bottom',
				},
				filter: ['in', ['get', 'icon'], ['literal', ['azul']]], // Only azul
			})

			// Roxo (Highest Layer)
			map.current.addLayer({
				id: 'stations-roxo',
				type: 'symbol',
				source: 'stations',
				layout: {
					'icon-image': '{icon}',
					'icon-size': 0.7,
					'icon-allow-overlap': true,
					'text-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-ignore-placement': true,
					'icon-anchor': 'bottom',
				},
				filter: ['in', ['get', 'icon'], ['literal', ['roxo']]], // Only roxo
			})

			map.current.addControl(
				new maplibregl.GeolocateControl({
					positionOptions: {
						enableHighAccuracy: true,
					},
					trackUserLocation: true,
				}),
			)

			map.current.addControl(new maplibregl.NavigationControl(), 'top-right')
			//@ts-ignore
			map.current.addControl(new CustomFullscreenControl())
			map.current.addControl(new RadioFilterControl(), 'top-left')

			// --- Tooltip Logic Start (Copied from MapaPlanejador) ---
			const stationLayers = [
				'stations-vermelho', // Updated Layer ID
				'stations-amarelo', // Updated Layer ID
				'stations-verde', // Updated Layer ID
				'stations-azul', // Updated Layer ID
				'stations-roxo', // Updated Layer ID
			] // Layers used in this component

			map.current.on('mousemove', (e) => {
				// --- Mobile device check ---
				if (isMobileDevice) {
					// On mobile, ensure cursor is default and no tooltip is shown
					map.current.getCanvas().style.cursor = '' // Reset cursor just in case
					if (stationPopup) {
						// Remove any lingering popup
						stationPopup.remove()
						stationPopup = null
						hoveredStationId = null
					}
					return // Don't proceed with hover/tooltip logic on mobile
				}
				// --- End of mobile check ---

				// Define a pixel buffer around the cursor
				const pixelBuffer = 3 // Use the smaller buffer size
				const queryBox = [
					[e.point.x - pixelBuffer, e.point.y - pixelBuffer],
					[e.point.x + pixelBuffer, e.point.y + pixelBuffer],
				]

				// Query features within the bounding box around the cursor
				const features = map.current.queryRenderedFeatures(queryBox, {
					layers: stationLayers,
				})

				// --- Step 1: Always determine if a station is hovered and set cursor ---
				let isHoveringStation = features.length > 0
				let feature = isHoveringStation ? features[0] : null

				// Ignore start/finish markers for hover effect too
				// (No start/finish markers in this component, but keep check just in case)
				if (
					feature &&
					(feature.properties.id === 'start' ||
						feature.properties.id === 'finish')
				) {
					isHoveringStation = false
					feature = null
				}

				map.current.getCanvas().style.cursor = isHoveringStation
					? 'pointer'
					: ''

				// --- Step 2: Handle Tooltip Logic ---
				// (No route check needed in this component)
				if (isHoveringStation) {
					const stationId = feature.properties.id
					const stationName = feature.properties.name

					// Only update if hovering a *new* station
					if (stationId !== hoveredStationId) {
						hoveredStationId = stationId

						const formatScore = (score) => {
							if (score === null || score === undefined) return 'N/A'
							return score.toFixed(1)
						}
						const formatPower = (power) => {
							if (power === null || power === undefined || power === 0)
								return 'N/A'
							return `${power.toFixed(0)} kW`
						}
						const formatRatings = (positives, negatives) => {
							const total = (positives || 0) + (negatives || 0)
							if (total === 0) return 'N/A'
							return total.toString()
						}

						// Calculate score using the helper function
						const calculatedScore = calculateScore(
							feature.properties.positives,
							feature.properties.negatives,
						)

						while (
							Math.abs(e.lngLat.lng - feature.geometry.coordinates[0]) > 180
						) {
							feature.geometry.coordinates[0] +=
								e.lngLat.lng > feature.geometry.coordinates[0] ? 360 : -360
						}

						if (!stationPopup) {
							stationPopup = new maplibregl.Popup({
								closeButton: false,
								closeOnClick: false,
								offset: {
									top: [0, 0],
									'top-left': [10, 10],
									'top-right': [-10, 10],
									bottom: [0, -30],
									'bottom-left': [10, -10],
									'bottom-right': [-10, -10],
									left: [10, -10],
									right: [-10, -10],
								},
								className: 'station-tooltip',
							})
						}

						const avgPower = formatPower(feature.properties.averagePower)
						// Use the calculated score for display
						const scoreDisplay =
							calculatedScore !== null ? formatScore(calculatedScore) : 'N/A'
						const city = feature.properties.city
						const state = feature.properties.state
						const location =
							city && state
								? `${formatText(city)}, ${formatState(state)}`
								: city || state || 'N/A'
						const positives = feature.properties.positives || 0
						const negatives = feature.properties.negatives || 0
						const ratingsCount = formatRatings(positives, negatives)

						const tooltipHTML = `
							<div class="station-tooltip-content">
								<div class="station-tooltip-name">${stationName}</div>
								<div class="station-tooltip-details">
									${avgPower !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-zap tooltip-icon"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg><span>${avgPower}</span></div>` : ''}
									${scoreDisplay !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star tooltip-icon"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg><span>${scoreDisplay}</span></div>` : ''}
									${ratingsCount !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide tooltip-icon"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg><span>${ratingsCount} ${positives + negatives === 1 ? 'avaliação' : 'avaliações'}</span></div>` : ''}
									${location !== 'N/A' ? `<div class="tooltip-detail-row"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin tooltip-icon"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg><span>${location}</span></div>` : ''}
								</div>
							</div>
						`

						stationPopup
							.setLngLat(feature.geometry.coordinates)
							.setHTML(tooltipHTML)
							.addTo(map.current)
					}
				} else {
					// No station hovered, remove popup if it exists
					if (hoveredStationId) {
						if (stationPopup) stationPopup.remove()
						stationPopup = null
						hoveredStationId = null
					}
				}
			}) // End mousemove handler
			// --- Tooltip Logic End ---

			// --- Click Handlers Start ---
			// Add click listeners for all station layers
			stationLayers.forEach((layerId) => {
				if (map.current.getLayer(layerId)) {
					// Check if layer exists
					map.current.on('click', layerId, (e) => {
						if (e.features.length > 0) {
							const { id, url } = e.features[0].properties
							openLink(url, id)
						}
					})
				}
			})
			// --- Click Handlers End ---

			interactionsSetupRef.current = true // Mark interactions as set up
		})

		map.current.on('moveend', () => {
			const bounds = map.current.getBounds()

			const nlat = bounds._ne.lat.toString()
			const nlng = bounds._ne.lng.toString()
			const slat = bounds._sw.lat.toString()
			const slng = bounds._sw.lng.toString()

			// get current power_filter query string - if it exists
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')

			fetcher.load(
				`/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}&power_filter=${powerFilter}`,
			)
		})
	}, [lng, lat, zoom])

	useEffect(() => {
		if (!map.current) return

		map.current.flyTo({
			center: [decode(userLon), decode(userLat)],
			zoom: zoomLevel,
		})
	}, [userLat, userLon, zoomLevel])

	useEffect(() => {
		if (!map.current) return

		const bounds = map.current.getBounds()
		const nlat = bounds._ne.lat.toString()
		const nlng = bounds._ne.lng.toString()
		const slat = bounds._sw.lat.toString()
		const slng = bounds._sw.lng.toString()

		// get current power_filter query string
		const searchParams = new URLSearchParams(window.location.search)
		const powerFilter = searchParams.get('power_filter')

		// Clear the loaded stations when filter changes
		setLoadedStations(new Map())

		fetcher.load(
			`/resources/bb-client?nlat=${nlat}&nlng=${nlng}&slat=${slat}&slng=${slng}&power_filter=${powerFilter}`,
		)
	}, [filter])

	useEffect(() => {
		if (iconsLoaded && fetcher.data && map.current) {
			// Ensure map and icons are ready
			const fetchedStationsMap = new Map()
			const searchParams = new URLSearchParams(window.location.search)
			const powerFilter = searchParams.get('power_filter')
			const powerFilterValue = powerFilter ? parseInt(powerFilter) : 0

			// Filter stations based on power filter before adding to map
			fetcher.data.stations.forEach((station) => {
				if (!powerFilterValue || station.averagePower >= powerFilterValue) {
					fetchedStationsMap.set(station.id, station)
				}
			})

			// Merge with existing stations, also applying power filter to cached stations
			let preliminaryCombinedStations = new Map()

			// Add filtered fetched stations
			for (const [id, station] of fetchedStationsMap) {
				preliminaryCombinedStations.set(id, station)
			}

			// Add filtered cached stations that aren't in the new fetch
			for (const [id, station] of loadedStations) {
				if (
					!fetchedStationsMap.has(id) &&
					(!powerFilterValue || station.averagePower >= powerFilterValue)
				) {
					preliminaryCombinedStations.set(id, station)
				}
			}

			let finalCombinedStations = preliminaryCombinedStations

			// Apply eviction strategy if exceeding the maximum limit
			if (preliminaryCombinedStations.size > maxStations) {
				const bounds = map.current.getBounds()
				const stationsArray = Array.from(preliminaryCombinedStations.entries())

				// Sort: Prioritize stations currently in view, then keep newest among those out of view
				stationsArray.sort(([, a], [, b]) => {
					const aInBounds = isStationInBounds(a, bounds)
					const bInBounds = isStationInBounds(b, bounds)
					if (aInBounds && !bInBounds) return -1 // Keep a (in bounds)
					if (!aInBounds && bInBounds) return 1 // Keep b (in bounds)
					// If both are in or out, prioritize the one from the latest fetch
					const aIsFromFetch = fetchedStationsMap.has(a.id)
					const bIsFromFetch = fetchedStationsMap.has(b.id)
					if (aIsFromFetch && !bIsFromFetch) return -1 // Keep a (newer)
					if (!aIsFromFetch && bIsFromFetch) return 1 // Keep b (newer)
					return 0 // Keep relative order if both same age or origin
				})

				finalCombinedStations = new Map(stationsArray.slice(0, maxStations))
			}

			// Compare before updating state
			let needsStateUpdate = false
			if (finalCombinedStations.size !== loadedStations.size) {
				needsStateUpdate = true
			} else {
				for (const [key] of finalCombinedStations) {
					if (!loadedStations.has(key)) {
						needsStateUpdate = true
						break
					}
				}
			}

			if (needsStateUpdate) {
				setLoadedStations(finalCombinedStations)
			}

			// Create features from all final stations
			const features = Array.from(finalCombinedStations.values()).map(
				(station) => ({
					type: 'Feature',
					properties: {
						id: station.id,
						url: station.url,
						name: station.name,
						averagePower: station.averagePower,
						positives: station.positives,
						negatives: station.negatives,
						city: station.city,
						state: station.state,
						icon_type: station.icon_type,
						icon: favorites.includes(station.id)
							? 'roxo'
							: iconTypeToColor[station.icon_type],
					},
					geometry: {
						type: 'Point',
						coordinates: [
							typeof station.longitude === 'string'
								? decode(station.longitude)
								: station.longitude,
							typeof station.latitude === 'string'
								? decode(station.latitude)
								: station.latitude,
						],
					},
				}),
			)

			// Update the map source
			const source = map.current.getSource('stations')
			if (source) {
				source.setData({
					type: 'FeatureCollection',
					features: features,
				})
			}
		}
	}, [
		fetcher.data,
		favorites,
		loadedStations,
		isStationInBounds,
		iconTypeToColor,
		iconsLoaded,
	])

	return (
		<div id="full" className="h-full w-full overflow-hidden rounded-lg">
			<div className="relative h-full w-full rounded-lg">
				<div ref={mapContainer} className="absolute h-full w-full" />
				{fetcher.state !== 'idle' && (
					<div className="absolute bottom-2 right-2 z-10 rounded-full bg-white/80 p-1 shadow backdrop-blur-sm transition-opacity duration-300 ease-in-out">
						<Loader2 className="h-4 w-4 animate-spin text-gray-500" />
					</div>
				)}
				{/* <div className="absolute bottom-0 left-20 flex items-start justify-start text-lg text-green-500">
				{fetcher.data && fetcher.data.stations.length > 0 ? (
					<p>{fetcher.data.stations.length} estações</p>
				) : (
					<p>0 estações</p>
				)}
			</div> */}
			</div>
		</div>
		// </>
	)
}
